import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import { User } from "@contexts/Auth";

import { useApiAuthPermissoesUsuario } from "@hooks/api/auth";
import useAuth from "@hooks/useAuth";
import { useCorban } from "@hooks/useCorban";
import { PRODUCT_CODE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";

type PermissionsContext = {
  permissions: Record<string, string>;
  routes: Record<string, { canPermission: boolean; isBlocked?: boolean }>;
  allowedRoutes: string[];
  homeRoute: string;
  isReady: boolean;
  hasPermission: (permission: string) => boolean;

  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsContext>({
  permissions: {},
  routes: {},
  allowedRoutes: [],
  homeRoute: "",
  isReady: false,
  hasPermission: () => false,
  loading: true,
});

export const PermissionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    fetchPermissoesUsuario,
    {
      data: fetchPermissionsData,
      loading: permissionsLoading,
      reset: resetFetchPermissions,
    },
  ] = useApiAuthPermissoesUsuario();

  const {
    user,
    updateUser,
    setProducts,
    hasProduct,
    hasProductBlocked,
    loading: userLoading,
  } = useAuth();
  const { isReady: isReadyCorban } = useCorban();
  const isReady = !!fetchPermissionsData;

  const [permissions, setPermissions] = useState<
    PermissionsContext["permissions"]
  >({});
  const [loading, setLoading] = useState(true);
  const clearData = useCallback(() => {
    setPermissions({});
    resetFetchPermissions();
  }, [resetFetchPermissions]);

  const hasPermission = useCallback(
    (permission: string) => !!permissions[permission],
    [permissions],
  );

  const routes: PermissionsContext["routes"] = useMemo(() => {
    const BLOCK_PRIVATE_CONSIGNED = true;
    return {
      "/servicos": { canPermission: hasPermission("add_contrato") },
      "/cartao/compra-cliente": {
        canPermission:
          [
            PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
            PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
          ].some(hasProduct) && hasPermission("add_contrato"),
        isBlocked: ![
          PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
          PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
        ].some((code) => !hasProductBlocked(code)),
      },
      "/cartao/cartao-beneficio": {
        canPermission:
          hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) &&
          hasPermission("add_contrato"),
        isBlocked: hasProductBlocked(PRODUCT_CODE.CARTAO_BENEFICIO),
      },
      "/cartao/agregacao-margem": {
        canPermission:
          hasProduct(PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO) &&
          hasPermission("add_contrato"),
        isBlocked: hasProductBlocked(PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO),
      },
      "/saque-complementar": {
        canPermission:
          hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) &&
          hasPermission("add_contrato"),
        isBlocked: hasProductBlocked(PRODUCT_CODE.SAQUE_COMPLEMENTAR),
      },
      "/contratos": { canPermission: true },
      "/selecao-corban": { canPermission: true },
      "/termos-de-uso": { canPermission: true },
      "/politica-de-privacidade": { canPermission: true },

      "/notificacoes": { canPermission: true },

      "/sem-acesso": { canPermission: true },

      "/nao-me-perturbe": { canPermission: hasPermission("add_contrato") },

      // Submenu keys
      "/cartao": {
        canPermission: [
          PRODUCT_CODE.CARTAO_BENEFICIO,
          PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO,
          PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
          PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
        ].some(hasProduct),
        isBlocked: ![
          PRODUCT_CODE.CARTAO_BENEFICIO,
          PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO,
          PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
          PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
        ].some((code) => !hasProductBlocked(code)),
      },

      "/consignado": {
        canPermission: [
          PRODUCT_CODE.CONSIGNADO_PRIVADO,
          PRODUCT_CODE.CONSIGNADO_PUBLICO,
        ].some(hasProduct),
        isBlocked: ![
          PRODUCT_CODE.CONSIGNADO_PRIVADO,
          PRODUCT_CODE.CONSIGNADO_PUBLICO,
        ].some((code) => !hasProductBlocked(code)),
      },
      "/consignado/consignado-privado": {
        canPermission:
          hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
          hasPermission("add_contrato") &&
          !BLOCK_PRIVATE_CONSIGNED,
        isBlocked: hasProductBlocked(PRODUCT_CODE.CONSIGNADO_PRIVADO),
      },
      "/consignado/consignado-publico": {
        canPermission: hasProduct(PRODUCT_CODE.CONSIGNADO_PUBLICO),
        isBlocked: hasProductBlocked(PRODUCT_CODE.CONSIGNADO_PUBLICO),
      },

      "/consignado/acompanhamento-proposta": {
        canPermission:
          hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
          hasPermission("add_contrato"),
        // isBlocked: hasProductBlocked(PRODUCT_CODE.CONSIGNADO_PRIVADO),
      },
      "/menu-usuarios": {
        canPermission:
          !!user?.allowCreateUser &&
          [
            "happy_can_create_user",
            "happy_can_view_user",
            "happy_can_change_user",
          ].some(hasPermission),
      },
      "/menu-favoritos": {
        canPermission: true,
      },
      "/usuarios": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
      "/usuarios/solicitacao": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
      "/usuarios/novo": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
    } satisfies PermissionsContext["routes"];
  }, [user, hasProduct, hasPermission, hasProductBlocked]);

  const allowedRoutes = useMemo(() => {
    return Object.keys(routes).filter((route) => {
      const { canPermission, isBlocked = false } = routes[route];
      return canPermission && !isBlocked;
    });
  }, [routes]);

  const homeRoute = useMemo(() => allowedRoutes[0] || "", [allowedRoutes]);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await fetchPermissoesUsuario();

      const userData: Partial<User> = {
        name: data.name,
        uniqueId: data.unique_id,
        identifier: data.identifier,
        enableFaceMatch: data.enable_face_match,
        isChecked: data.is_checked,
        checkUrl: data.check_url,
        supportChat: data.possui_chat_suporte,
        allowIlliterate: !!data.corban?.permite_analfabeto,
        allowLegalRepresentative: !!data.corban?.permite_representante,
        allowCardLegalRepresentative:
          !!data.corban?.permite_representante_cartao,
        allowCreateUser: !!data.corban?.permite_criar_usuario_originacao,
        id: data.id,
      };

      updateUser(userData);

      setPermissions(
        data.permissions.reduce(
          (permissionsHashList, permission) => ({
            ...permissionsHashList,
            [permission]: true,
          }),
          {},
        ),
      );

      setProducts(
        data.produtos.map((product) => ({
          nome: product.nome,
          tipo_produto: getProductTypeFromProductCode(product.cd_produto),
          codigo_produto: product.cd_produto,
        })),
      );
    } catch (e) {
      if (
        !(e instanceof ApiInterceptorError) ||
        !axios.isCancel(e.sourceError)
      ) {
        clearData();
      }
    } finally {
      setLoading(false);
    }
  }, [fetchPermissoesUsuario, updateUser, setProducts, clearData]);

  useEffect(() => {
    if (!userLoading) {
      if (user?.refreshToken) {
        if (isReadyCorban) void fetchPermissions();
      } else {
        clearData();
        setLoading(false);
      }
    }
  }, [
    user?.refreshToken,
    userLoading,
    fetchPermissions,
    clearData,
    isReadyCorban,
  ]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        routes,
        allowedRoutes,
        homeRoute,
        isReady,
        hasPermission,
        loading: loading || permissionsLoading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
